// The single "stop impersonating" action, shared by the two chrome surfaces
// (wiki/feature-impersonation.md AC13.8): the layout banner and the account
// menu. Both render the same busy/failure state because they drive the same
// session — a stop started from one must disable the other.
//
// The aggregate id is the ADMIN's, from `$auth.user.impersonation.adminUserId`;
// under the effective-account swap `$auth.user.id` is the target, and posting it
// leaves the admin unable to leave the session (AC12.4).

import type { UserId } from '@voicedojo/backend-types';
import { UiKeys } from '@voicedojo/backend-types';
import { resolve } from '$app/paths';
import { ApiError } from '$lib/api/client';
import { stopImpersonation } from '$lib/api/impersonation';
import { t } from '$lib/i18n';

let busy = $state(false);
let error = $state<string | null>(null);

export const impersonationStop = {
  get busy(): boolean {
    return busy;
  },
  /** Inline failure text — surfaced in place, never as a toast (AC13.6). */
  get error(): string | null {
    return error;
  },
  async run(adminUserId: UserId, targetUserId: UserId): Promise<void> {
    busy = true;
    error = null;
    try {
      await stopImpersonation(adminUserId);
    } catch (e) {
      if (!(e instanceof ApiError)) throw e;
      error = e.message || t(UiKeys.Impersonation.FailedToStop);
      busy = false;
      return;
    }
    // A full load of the admin console on the user just left (AC13.8): the admin's
    // own identity never stays on a product page, and no state resolved under the
    // target survives.
    window.location.assign(resolve('/admin/users/[id]', { id: targetUserId }));
  },
};
